<template>
    <div>
        <LoadingModal v-if="loading" @close="$emit('abort')" />
        <TprmModal
            v-else-if="error && !document"
            additional-class-name="ViewEntityDocumentProcedure"
            open
            size="md"
            title="Document"
            allow-close
            @close="$emit('abort')"
        >
            <div class="ViewEntityDocumentProcedure__error">
                {{ error }}
            </div>
            <template #footer>
                <NewButton
                    variant="outline"
                    color="neutral"
                    @click="$emit('abort')"
                >
                    Close
                </NewButton>
            </template>
        </TprmModal>
        <DocumentDetailsModal
            v-else-if="document"
            :documents="[document]"
            :requirement-label="document.filename || ''"
            email=""
            uploaded-at=""
            @close="$emit('abort')"
        >
            <template #header-actions>
                <div class="ViewEntityDocumentProcedure__tagActions">
                    <TagSelector
                        :rp-name="rpName"
                        resource-type="DOCUMENT"
                        :value="currentTags"
                        @input="currentTags = $event"
                    />
                    <NewButton
                        color="primary"
                        size="sm"
                        :loading="savingTags"
                        :disabled="!hasTagChanges || savingTags"
                        @click="onSaveTags"
                    >
                        Save Changes
                    </NewButton>
                </div>
            </template>
        </DocumentDetailsModal>
    </div>
</template>

<style lang="scss">
.ViewEntityDocumentProcedure {
    &__error {
        color: var(--feedback-error-color);
        font-size: 13px;
        line-height: 1.35;
    }

    &__tagActions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { VerificationDocument } from '@evidentid/gateway-api-client/src/models/submission-tprm/VerificationDocument.model';

    import LoadingModal from '@/components/loading-modal/LoadingModal.vue';
    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import DocumentDetailsModal from '@/modules/entity-documents/components/DocumentDetailsModal.vue';
    import { useEntityEventsStore } from '@/modules/entity-events/store';
    import TagSelector from '@/modules/entity-tags/components/TagSelector/TagSelector.vue';
    import { useEntityTagsStore } from '@/modules/entity-tags/store';

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    export default defineComponent({
        name: 'ViewEntityDocumentProcedure',
        components: { TprmModal, NewButton, LoadingModal, DocumentDetailsModal, TagSelector },
        props: {
            // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't
            // warn "Extraneous non-props attributes (uuid)" (see
            // UploadEntityDocumentProcedure.vue).
            uuid: {
                type: String,
                default: '',
            },
            rpName: {
                type: String as PropType<string>,
                required: true,
            },
            entityId: {
                type: String as PropType<string>,
                required: true,
            },
            documentId: {
                type: String as PropType<string>,
                required: true,
            },
            initialTags: {
                type: Array as PropType<string[]>,
                default: (): string[] => [],
            },
        },
        emits: [ 'abort', 'finish' ],
        data() {
            return {
                loading: true,
                error: '',
                document: null as VerificationDocument | null,
                savingTags: false,
                currentTags: [] as string[],
            };
        },
        computed: {
            hasTagChanges(): boolean {
                if (this.currentTags.length !== this.initialTags.length) {
                    return true;
                }
                const initialSet = new Set(this.initialTags);
                return this.currentTags.some((t: string) => !initialSet.has(t));
            },
        },
        watch: {
            initialTags: {
                immediate: true,
                handler(tags: string[]) {
                    this.currentTags = [ ...tags ];
                },
            },
        },
        mounted() {
            this.onLoad();
        },
        methods: {
            async onLoad(): Promise<void> {
                try {
                    const gatewayClient = (this as any).$gatewayClient;
                    const response = await gatewayClient.submissionTprmUi().getEntityDocument(
                        this.rpName, this.entityId, this.documentId,
                    );
                    this.document = {
                        mimeType: response.mimeType,
                        data: response.document,
                        filename: response.filename,
                    };
                } catch (e) {
                    this.error = 'Failed to load document. Please try again.';
                } finally {
                    this.loading = false;
                }
            },
            async onSaveTags(): Promise<void> {
                this.savingTags = true;
                this.error = '';
                try {
                    const gatewayClient = (this as any).$gatewayClient;
                    await gatewayClient.tprmUi().patchResourceTags(
                        this.rpName, this.entityId, {
                            resourceType: 'DOCUMENT',
                            resourceId: this.documentId,
                            tags: this.currentTags,
                        },
                    );
                    useEntityEventsStore().loadActivityHistory({
                        rpName: this.rpName,
                        entityId: this.entityId,
                        filters: {},
                    });
                    useEntityTagsStore().loadTags({ rpName: this.rpName });
                    this.$emit('finish');
                } catch (e) {
                    this.error = 'Failed to save tags. Please try again.';
                } finally {
                    this.savingTags = false;
                }
            },
        },
    });
</script>
